import React, { useState, useEffect } from 'react';
import { Container, Modal, Tab, Tabs, Button, Table, Form, Row, Col, Alert, Spinner } from 'react-bootstrap';
import { API_URL } from '../../utils/constants';
import './SetupWorkOrdersScreen.css';

function SetupWorkOrdersScreen() {
  const [showModal, setShowModal] = useState(true);
  const [activeTab, setActiveTab] = useState('request-type');

  // Request Type state
  const [requestTypes, setRequestTypes] = useState([]);
  const [newRequestType, setNewRequestType] = useState({ name: '', active: true });
  const [loadingRequestTypes, setLoadingRequestTypes] = useState(true);
  const [savingRequestType, setSavingRequestType] = useState(false);
  
  // Request Type Modal state
  const [showRequestTypeModal, setShowRequestTypeModal] = useState(false);
  const [requestTypeModalMode, setRequestTypeModalMode] = useState('add'); // 'add' or 'edit'
  const [selectedRequestType, setSelectedRequestType] = useState(null);
  const [requestTypeForm, setRequestTypeForm] = useState({ name: '', active: true, sort_order: 0 });
  const [requestTypeErrors, setRequestTypeErrors] = useState({});

  // Location state
  const [locations, setLocations] = useState([]);
  const [newLocation, setNewLocation] = useState({ name: '', active: true });
  const [loadingLocations, setLoadingLocations] = useState(true);
  const [savingLocation, setSavingLocation] = useState(false);
  
  // Location Modal state
  const [showLocationModal, setShowLocationModal] = useState(false);
  const [locationModalMode, setLocationModalMode] = useState('add'); // 'add' or 'edit'
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [locationForm, setLocationForm] = useState({ name: '', active: true, sort_order: 0 });
  const [locationErrors, setLocationErrors] = useState({});

  // Taken From state
  const [takenFrom, setTakenFrom] = useState([]);
  const [newTakenFrom, setNewTakenFrom] = useState({ name: '', active: true });
  const [loadingTakenFrom, setLoadingTakenFrom] = useState(true);
  const [savingTakenFrom, setSavingTakenFrom] = useState(false);
  
  // Taken From Modal state
  const [showTakenFromModal, setShowTakenFromModal] = useState(false);
  const [takenFromModalMode, setTakenFromModalMode] = useState('add'); // 'add' or 'edit'
  const [selectedTakenFrom, setSelectedTakenFrom] = useState(null);
  const [takenFromForm, setTakenFromForm] = useState({ name: '', active: true, sort_order: 0 });
  const [takenFromErrors, setTakenFromErrors] = useState({});

  // Error and success messages
  const [message, setMessage] = useState({ text: '', variant: '', show: false });

  // Fetch data on component mount
  useEffect(() => {
    fetchRequestTypes();
    fetchLocations();
    fetchTakenFrom();
  }, []);

  // API Functions for Request Types
  const fetchRequestTypes = async () => {
    try {
      setLoadingRequestTypes(true);
      const response = await fetch(`${API_URL}/work-order-request-types`);
      if (!response.ok) throw new Error('Failed to fetch request types');
      const data = await response.json();
      setRequestTypes(data);
    } catch (error) {
      console.error('Error fetching request types:', error);
      setMessage({ text: 'Failed to load request types', variant: 'danger', show: true });
    } finally {
      setLoadingRequestTypes(false);
    }
  };

  const addRequestType = async () => {
    if (!newRequestType.name.trim()) return;
    
    try {
      setSavingRequestType(true);
      const response = await fetch(`${API_URL}/work-order-request-types`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newRequestType)
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to create request type');
      }
      
      const newType = await response.json();
      setRequestTypes([...requestTypes, newType]);
      setNewRequestType({ name: '', active: true });
      setMessage({ text: 'Request type created successfully', variant: 'success', show: true });
    } catch (error) {
      console.error('Error creating request type:', error);
      setMessage({ text: error.message, variant: 'danger', show: true });
    } finally {
      setSavingRequestType(false);
    }
  };

  const updateRequestType = async (id, updates) => {
    try {
      const response = await fetch(`${API_URL}/work-order-request-types/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updates)
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to update request type');
      }
      
      const updatedType = await response.json();
      setRequestTypes(requestTypes.map(rt => rt.id === id ? updatedType : rt));
      setMessage({ text: 'Request type updated successfully', variant: 'success', show: true });
    } catch (error) {
      console.error('Error updating request type:', error);
      setMessage({ text: error.message, variant: 'danger', show: true });
    }
  };

  const deleteRequestType = async (id) => {
    if (!window.confirm('Are you sure you want to delete this request type?')) return;
    
    try {
      const response = await fetch(`${API_URL}/work-order-request-types/${id}`, {
        method: 'DELETE'
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to delete request type');
      }
      
      setRequestTypes(requestTypes.filter(rt => rt.id !== id));
      setMessage({ text: 'Request type deleted successfully', variant: 'success', show: true });
    } catch (error) {
      console.error('Error deleting request type:', error);
      setMessage({ text: error.message, variant: 'danger', show: true });
    }
  };

  // Request Type Modal handlers
  const handleAddRequestType = () => {
    setRequestTypeModalMode('add');
    setSelectedRequestType(null);
    setRequestTypeForm({ name: '', active: true, sort_order: 0 });
    setRequestTypeErrors({});
    setShowRequestTypeModal(true);
  };

  const handleEditRequestType = (requestType) => {
    setRequestTypeModalMode('edit');
    setSelectedRequestType(requestType);
    setRequestTypeForm({ name: requestType.name, active: requestType.active, sort_order: requestType.sort_order || 0 });
    setRequestTypeErrors({});
    setShowRequestTypeModal(true);
  };

  const handleRequestTypeInputChange = (field, value) => {
    setRequestTypeForm(prev => ({ ...prev, [field]: value }));
    // Clear error for this field
    if (requestTypeErrors[field]) {
      setRequestTypeErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  const validateRequestTypeForm = () => {
    const errors = {};
    if (!requestTypeForm.name.trim()) {
      errors.name = 'Name is required';
    }
    return errors;
  };

  const handleSaveRequestType = async () => {
    const errors = validateRequestTypeForm();
    if (Object.keys(errors).length > 0) {
      setRequestTypeErrors(errors);
      return;
    }

    try {
      setSavingRequestType(true);
      if (requestTypeModalMode === 'add') {
        const response = await fetch(`${API_URL}/work-order-request-types`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(requestTypeForm)
        });
        
        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.message || 'Failed to create request type');
        }
        
        const newType = await response.json();
        setRequestTypes([...requestTypes, newType]);
        setMessage({ text: 'Request type created successfully', variant: 'success', show: true });
      } else {
        await updateRequestType(selectedRequestType.id, requestTypeForm);
      }
      
      setShowRequestTypeModal(false);
      setRequestTypeForm({ name: '', active: true });
    } catch (error) {
      setMessage({ text: error.message, variant: 'danger', show: true });
    } finally {
      setSavingRequestType(false);
    }
  };

  const handleCloseRequestTypeModal = () => {
    setShowRequestTypeModal(false);
    setRequestTypeForm({ name: '', active: true, sort_order: 0 });
    setRequestTypeErrors({});
  };

  // API Functions for Locations
  const fetchLocations = async () => {
    try {
      setLoadingLocations(true);
      const response = await fetch(`${API_URL}/work-order-locations`);
      if (!response.ok) throw new Error('Failed to fetch locations');
      const data = await response.json();
      setLocations(data);
    } catch (error) {
      console.error('Error fetching locations:', error);
      setMessage({ text: 'Failed to load locations', variant: 'danger', show: true });
    } finally {
      setLoadingLocations(false);
    }
  };

  const addLocation = async () => {
    if (!newLocation.name.trim()) return;
    
    try {
      setSavingLocation(true);
      const response = await fetch(`${API_URL}/work-order-locations`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newLocation)
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to create location');
      }
      
      const newLoc = await response.json();
      setLocations([...locations, newLoc]);
      setNewLocation({ name: '', active: true });
      setMessage({ text: 'Location created successfully', variant: 'success', show: true });
    } catch (error) {
      console.error('Error creating location:', error);
      setMessage({ text: error.message, variant: 'danger', show: true });
    } finally {
      setSavingLocation(false);
    }
  };

  const updateLocation = async (id, updates) => {
    try {
      const response = await fetch(`${API_URL}/work-order-locations/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updates)
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to update location');
      }
      
      const updatedLoc = await response.json();
      setLocations(locations.map(loc => loc.id === id ? updatedLoc : loc));
      setMessage({ text: 'Location updated successfully', variant: 'success', show: true });
    } catch (error) {
      console.error('Error updating location:', error);
      setMessage({ text: error.message, variant: 'danger', show: true });
    }
  };

  const deleteLocation = async (id) => {
    if (!window.confirm('Are you sure you want to delete this location?')) return;
    
    try {
      const response = await fetch(`${API_URL}/work-order-locations/${id}`, {
        method: 'DELETE'
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to delete location');
      }
      
      setLocations(locations.filter(loc => loc.id !== id));
      setMessage({ text: 'Location deleted successfully', variant: 'success', show: true });
    } catch (error) {
      console.error('Error deleting location:', error);
      setMessage({ text: error.message, variant: 'danger', show: true });
    }
  };

  // Location Modal handlers
  const handleAddLocation = () => {
    setLocationModalMode('add');
    setSelectedLocation(null);
    setLocationForm({ name: '', active: true, sort_order: 0 });
    setLocationErrors({});
    setShowLocationModal(true);
  };

  const handleEditLocation = (location) => {
    setLocationModalMode('edit');
    setSelectedLocation(location);
    setLocationForm({ name: location.name, active: location.active, sort_order: location.sort_order || 0 });
    setLocationErrors({});
    setShowLocationModal(true);
  };

  const handleLocationInputChange = (field, value) => {
    setLocationForm(prev => ({ ...prev, [field]: value }));
    // Clear error for this field
    if (locationErrors[field]) {
      setLocationErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  const validateLocationForm = () => {
    const errors = {};
    if (!locationForm.name.trim()) {
      errors.name = 'Name is required';
    }
    return errors;
  };

  const handleSaveLocation = async () => {
    const errors = validateLocationForm();
    if (Object.keys(errors).length > 0) {
      setLocationErrors(errors);
      return;
    }

    try {
      setSavingLocation(true);
      if (locationModalMode === 'add') {
        const response = await fetch(`${API_URL}/work-order-locations`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(locationForm)
        });
        
        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.message || 'Failed to create location');
        }
        
        const newLoc = await response.json();
        setLocations([...locations, newLoc]);
        setMessage({ text: 'Location created successfully', variant: 'success', show: true });
      } else {
        await updateLocation(selectedLocation.id, locationForm);
      }
      
      setShowLocationModal(false);
      setLocationForm({ name: '', active: true });
    } catch (error) {
      setMessage({ text: error.message, variant: 'danger', show: true });
    } finally {
      setSavingLocation(false);
    }
  };

  const handleCloseLocationModal = () => {
    setShowLocationModal(false);
    setLocationForm({ name: '', active: true, sort_order: 0 });
    setLocationErrors({});
  };

  // API Functions for Taken From
  const fetchTakenFrom = async () => {
    try {
      setLoadingTakenFrom(true);
      const response = await fetch(`${API_URL}/work-order-taken-from`);
      if (!response.ok) throw new Error('Failed to fetch taken from sources');
      const data = await response.json();
      setTakenFrom(data);
    } catch (error) {
      console.error('Error fetching taken from sources:', error);
      setMessage({ text: 'Failed to load taken from sources', variant: 'danger', show: true });
    } finally {
      setLoadingTakenFrom(false);
    }
  };

  const addTakenFrom = async () => {
    if (!newTakenFrom.name.trim()) return;
    
    try {
      setSavingTakenFrom(true);
      const response = await fetch(`${API_URL}/work-order-taken-from`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newTakenFrom)
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to create taken from source');
      }
      
      const newSource = await response.json();
      setTakenFrom([...takenFrom, newSource]);
      setNewTakenFrom({ name: '', active: true });
      setMessage({ text: 'Taken from source created successfully', variant: 'success', show: true });
    } catch (error) {
      console.error('Error creating taken from source:', error);
      setMessage({ text: error.message, variant: 'danger', show: true });
    } finally {
      setSavingTakenFrom(false);
    }
  };

  const updateTakenFrom = async (id, updates) => {
    try {
      const response = await fetch(`${API_URL}/work-order-taken-from/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updates)
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to update taken from source');
      }
      
      const updatedSource = await response.json();
      setTakenFrom(takenFrom.map(tf => tf.id === id ? updatedSource : tf));
      setMessage({ text: 'Taken from source updated successfully', variant: 'success', show: true });
    } catch (error) {
      console.error('Error updating taken from source:', error);
      setMessage({ text: error.message, variant: 'danger', show: true });
    }
  };

  const deleteTakenFrom = async (id) => {
    if (!window.confirm('Are you sure you want to delete this taken from source?')) return;
    
    try {
      const response = await fetch(`${API_URL}/work-order-taken-from/${id}`, {
        method: 'DELETE'
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to delete taken from source');
      }
      
      setTakenFrom(takenFrom.filter(tf => tf.id !== id));
      setMessage({ text: 'Taken from source deleted successfully', variant: 'success', show: true });
    } catch (error) {
      console.error('Error deleting taken from source:', error);
      setMessage({ text: error.message, variant: 'danger', show: true });
    }
  };

  // Taken From Modal handlers
  const handleAddTakenFrom = () => {
    setTakenFromModalMode('add');
    setSelectedTakenFrom(null);
    setTakenFromForm({ name: '', active: true, sort_order: 0 });
    setTakenFromErrors({});
    setShowTakenFromModal(true);
  };

  const handleEditTakenFrom = (source) => {
    setTakenFromModalMode('edit');
    setSelectedTakenFrom(source);
    setTakenFromForm({ name: source.name, active: source.active, sort_order: source.sort_order || 0 });
    setTakenFromErrors({});
    setShowTakenFromModal(true);
  };

  const handleTakenFromInputChange = (field, value) => {
    setTakenFromForm(prev => ({ ...prev, [field]: value }));
    // Clear error for this field
    if (takenFromErrors[field]) {
      setTakenFromErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  const validateTakenFromForm = () => {
    const errors = {};
    if (!takenFromForm.name.trim()) {
      errors.name = 'Name is required';
    }
    return errors;
  };

  const handleSaveTakenFrom = async () => {
    const errors = validateTakenFromForm();
    if (Object.keys(errors).length > 0) {
      setTakenFromErrors(errors);
      return;
    }

    try {
      setSavingTakenFrom(true);
      if (takenFromModalMode === 'add') {
        const response = await fetch(`${API_URL}/work-order-taken-from`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(takenFromForm)
        });
        
        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.message || 'Failed to create taken from source');
        }
        
        const newSource = await response.json();
        setTakenFrom([...takenFrom, newSource]);
        setMessage({ text: 'Taken from source created successfully', variant: 'success', show: true });
      } else {
        await updateTakenFrom(selectedTakenFrom.id, takenFromForm);
      }
      
      setShowTakenFromModal(false);
      setTakenFromForm({ name: '', active: true });
    } catch (error) {
      setMessage({ text: error.message, variant: 'danger', show: true });
    } finally {
      setSavingTakenFrom(false);
    }
  };

  const handleCloseTakenFromModal = () => {
    setShowTakenFromModal(false);
    setTakenFromForm({ name: '', active: true, sort_order: 0 });
    setTakenFromErrors({});
  };

  // Toggle active status handlers
  const toggleRequestTypeActive = (id) => {
    const requestType = requestTypes.find(rt => rt.id === id);
    if (requestType) {
      updateRequestType(id, { name: requestType.name, active: !requestType.active });
    }
  };

  const toggleLocationActive = (id) => {
    const location = locations.find(loc => loc.id === id);
    if (location) {
      updateLocation(id, { name: location.name, active: !location.active });
    }
  };

  const toggleTakenFromActive = (id) => {
    const source = takenFrom.find(tf => tf.id === id);
    if (source) {
      updateTakenFrom(id, { name: source.name, active: !source.active });
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
  };

  const handleModalClose = () => {
    setShowModal(false);
    setActiveTab('request-type');
  };

  const handleSaveAndClose = () => {
    // Save any changes and close modal
    setShowModal(false);
    setActiveTab('request-type');
  };

  const handleSave = () => {
    // Save any changes but keep modal open
    console.log('Saving changes...');
  };

  // Auto-hide messages after 5 seconds
  useEffect(() => {
    if (message.show) {
      const timer = setTimeout(() => {
        setMessage({ text: '', variant: '', show: false });
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [message.show]);

  return (
    <Container fluid className="setup-work-orders-screen">
      <div className="screen-header">
        <h1>Setup - Work Orders</h1>
        <p>Configure work orders settings and management</p>
      </div>

      <div className="screen-content">
        {/* Message Alert */}
        {message.show && (
          <Alert 
            variant={message.variant} 
            dismissible 
            onClose={() => setMessage({ text: '', variant: '', show: false })}
            className="mb-3"
          >
            {message.text}
          </Alert>
        )}

        {/* Main Setup Modal */}
        <Modal 
          show={showModal} 
          onHide={handleCloseModal}
          size="xl"
          backdrop="static"
          keyboard={false}
          className="setup-modal"
        >
          <Modal.Header>
            <Modal.Title>Setup - Work Orders</Modal.Title>
            <Button
              variant="link"
              onClick={handleModalClose}
              className="close-button"
            >
              ×
            </Button>
          </Modal.Header>
          
          <Modal.Body>
            <Tabs
              activeKey={activeTab}
              onSelect={(k) => setActiveTab(k)}
              className="setup-tabs"
            >
              <Tab eventKey="request-type" title="Request Type">
                <div className="tab-content">
                  <div className="tab-header">
                    <h4>Request Types</h4>
                    <Button 
                      variant="success" 
                      onClick={handleAddRequestType}
                      size="sm"
                    >
                      ➕ Add Request Type
                    </Button>
                  </div>
                  
                  {loadingRequestTypes ? (
                    <div className="text-center py-4">
                      <div className="spinner-border" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </div>
                    </div>
                  ) : (
                    <div className="table-responsive">
                                             <Table striped bordered hover>
                         <thead>
                           <tr>
                             <th>Name</th>
                             <th>Sort Order</th>
                             <th>Active</th>
                             <th>Actions</th>
                           </tr>
                         </thead>
                         <tbody>
                           {requestTypes.map((rt) => (
                             <tr key={rt.id}>
                               <td>{rt.name}</td>
                               <td>{rt.sort_order || 0}</td>
                               <td>
                                 <span className={`badge ${rt.active ? 'bg-success' : 'bg-secondary'}`}>
                                   {rt.active ? 'Active' : 'Inactive'}
                                 </span>
                               </td>
                               <td>
                                 <Button
                                   variant="outline-primary"
                                   size="sm"
                                   onClick={() => handleEditRequestType(rt)}
                                   className="me-2"
                                 >
                                   ✏️ Edit
                                 </Button>
                                 <Button
                                   variant="outline-danger"
                                   size="sm"
                                   onClick={() => deleteRequestType(rt.id)}
                                 >
                                   🗑️ Delete
                                 </Button>
                               </td>
                             </tr>
                           ))}
                         </tbody>
                       </Table>
                    </div>
                  )}
                </div>
              </Tab>

              <Tab eventKey="location" title="Location">
                <div className="tab-content">
                  <div className="tab-header">
                    <h4>Locations</h4>
                    <Button 
                      variant="success" 
                      onClick={handleAddLocation}
                      size="sm"
                    >
                      ➕ Add Location
                    </Button>
                  </div>
                  
                  {loadingLocations ? (
                    <div className="text-center py-4">
                      <div className="spinner-border" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </div>
                    </div>
                  ) : (
                    <div className="table-responsive">
                                             <Table striped bordered hover>
                         <thead>
                           <tr>
                             <th>Name</th>
                             <th>Sort Order</th>
                             <th>Active</th>
                             <th>Actions</th>
                           </tr>
                         </thead>
                         <tbody>
                           {locations.map((loc) => (
                             <tr key={loc.id}>
                               <td>{loc.name}</td>
                               <td>{loc.sort_order || 0}</td>
                               <td>
                                 <span className={`badge ${loc.active ? 'bg-success' : 'bg-secondary'}`}>
                                   {loc.active ? 'Active' : 'Inactive'}
                                 </span>
                               </td>
                               <td>
                                 <Button
                                   variant="outline-primary"
                                   size="sm"
                                   onClick={() => handleEditLocation(loc)}
                                   className="me-2"
                                 >
                                   ✏️ Edit
                                 </Button>
                                 <Button
                                   variant="outline-danger"
                                   size="sm"
                                   onClick={() => deleteLocation(loc.id)}
                                 >
                                   🗑️ Delete
                                 </Button>
                               </td>
                             </tr>
                           ))}
                         </tbody>
                       </Table>
                    </div>
                  )}
                </div>
              </Tab>

              <Tab eventKey="taken-from" title="Taken From">
                <div className="tab-content">
                  <div className="tab-header">
                    <h4>Taken From</h4>
                    <Button 
                      variant="success" 
                      onClick={handleAddTakenFrom}
                      size="sm"
                    >
                      ➕ Add Taken From
                    </Button>
                  </div>
                  
                  {loadingTakenFrom ? (
                    <div className="text-center py-4">
                      <div className="spinner-border" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </div>
                    </div>
                  ) : (
                    <div className="table-responsive">
                                             <Table striped bordered hover>
                         <thead>
                           <tr>
                             <th>Name</th>
                             <th>Sort Order</th>
                             <th>Active</th>
                             <th>Actions</th>
                           </tr>
                         </thead>
                         <tbody>
                           {takenFrom.map((tf) => (
                             <tr key={tf.id}>
                               <td>{tf.name}</td>
                               <td>{tf.sort_order || 0}</td>
                               <td>
                                 <span className={`badge ${tf.active ? 'bg-success' : 'bg-secondary'}`}>
                                   {tf.active ? 'Active' : 'Inactive'}
                                 </span>
                               </td>
                               <td>
                                 <Button
                                   variant="outline-primary"
                                   size="sm"
                                   onClick={() => handleEditTakenFrom(tf)}
                                   className="me-2"
                                 >
                                   ✏️ Edit
                                 </Button>
                                 <Button
                                   variant="outline-danger"
                                   size="sm"
                                   onClick={() => deleteTakenFrom(tf.id)}
                                 >
                                   🗑️ Delete
                                 </Button>
                               </td>
                             </tr>
                           ))}
                         </tbody>
                       </Table>
                    </div>
                  )}
                </div>
              </Tab>
            </Tabs>
          </Modal.Body>
          
          <Modal.Footer>
            <Button onClick={handleSaveAndClose}>
              Save & Close
            </Button>
            <Button onClick={handleSave}>
              Save
            </Button>
            <Button onClick={handleModalClose}>
              Close
            </Button>
          </Modal.Footer>
        </Modal>

        {/* Request Type Modal */}
        <Modal 
          show={showRequestTypeModal} 
          onHide={handleCloseRequestTypeModal}
          size="lg"
        >
          <Modal.Header closeButton>
            <Modal.Title>
              {requestTypeModalMode === 'add' ? 'Add Request Type' : 'Edit Request Type'}
            </Modal.Title>
          </Modal.Header>
          
          <Modal.Body>
            <Form>
              <Form.Group className="mb-3">
                <Form.Label>Name *</Form.Label>
                <Form.Control
                  type="text"
                  value={requestTypeForm.name}
                  onChange={(e) => handleRequestTypeInputChange('name', e.target.value)}
                  isInvalid={!!requestTypeErrors.name}
                  placeholder="e.g., Maintenance, Cleaning, Repair"
                />
                <Form.Control.Feedback type="invalid">
                  {requestTypeErrors.name}
                </Form.Control.Feedback>
              </Form.Group>

                             <Form.Group className="mb-3">
                 <Form.Label>Sort Order</Form.Label>
                 <Form.Control
                   type="number"
                   min="0"
                   value={requestTypeForm.sort_order}
                   onChange={(e) => handleRequestTypeInputChange('sort_order', parseInt(e.target.value) || 0)}
                   placeholder="0"
                 />
               </Form.Group>

               <Form.Group className="mb-3">
                 <Form.Check
                   type="checkbox"
                   label="Active"
                   checked={requestTypeForm.active}
                   onChange={(e) => handleRequestTypeInputChange('active', e.target.checked)}
                 />
               </Form.Group>
            </Form>
          </Modal.Body>
          
          <Modal.Footer>
            <Button variant="secondary" onClick={handleCloseRequestTypeModal}>
              Cancel
            </Button>
            <Button 
              variant="primary" 
              onClick={handleSaveRequestType}
              disabled={savingRequestType}
            >
              {savingRequestType ? 'Saving...' : 'Save'}
            </Button>
          </Modal.Footer>
        </Modal>

        {/* Location Modal */}
        <Modal 
          show={showLocationModal} 
          onHide={handleCloseLocationModal}
          size="lg"
        >
          <Modal.Header closeButton>
            <Modal.Title>
              {locationModalMode === 'add' ? 'Add Location' : 'Edit Location'}
            </Modal.Title>
          </Modal.Header>
          
          <Modal.Body>
            <Form>
              <Form.Group className="mb-3">
                <Form.Label>Name *</Form.Label>
                <Form.Control
                  type="text"
                  value={locationForm.name}
                  onChange={(e) => handleLocationInputChange('name', e.target.value)}
                  isInvalid={!!locationErrors.name}
                  placeholder="e.g., Pool Area, Lobby, Guest Rooms"
                />
                <Form.Control.Feedback type="invalid">
                  {locationErrors.name}
                </Form.Control.Feedback>
              </Form.Group>

                             <Form.Group className="mb-3">
                 <Form.Label>Sort Order</Form.Label>
                 <Form.Control
                   type="number"
                   min="0"
                   value={locationForm.sort_order}
                   onChange={(e) => handleLocationInputChange('sort_order', parseInt(e.target.value) || 0)}
                   placeholder="0"
                 />
               </Form.Group>

               <Form.Group className="mb-3">
                 <Form.Check
                   type="checkbox"
                   label="Active"
                   checked={locationForm.active}
                   onChange={(e) => handleLocationInputChange('active', e.target.checked)}
                 />
               </Form.Group>
            </Form>
          </Modal.Body>
          
          <Modal.Footer>
            <Button variant="secondary" onClick={handleCloseLocationModal}>
              Cancel
            </Button>
            <Button 
              variant="primary" 
              onClick={handleSaveLocation}
              disabled={savingLocation}
            >
              {savingLocation ? 'Saving...' : 'Save'}
            </Button>
          </Modal.Footer>
        </Modal>

        {/* Taken From Modal */}
        <Modal 
          show={showTakenFromModal} 
          onHide={handleCloseTakenFromModal}
          size="lg"
        >
          <Modal.Header closeButton>
            <Modal.Title>
              {takenFromModalMode === 'add' ? 'Add Taken From' : 'Edit Taken From'}
            </Modal.Title>
          </Modal.Header>
          
          <Modal.Body>
            <Form>
              <Form.Group className="mb-3">
                <Form.Label>Name *</Form.Label>
                <Form.Control
                  type="text"
                  value={takenFromForm.name}
                  onChange={(e) => handleTakenFromInputChange('name', e.target.value)}
                  isInvalid={!!takenFromErrors.name}
                  placeholder="e.g., Front Desk, Housekeeping, Guest Request"
                />
                <Form.Control.Feedback type="invalid">
                  {takenFromErrors.name}
                </Form.Control.Feedback>
              </Form.Group>

                             <Form.Group className="mb-3">
                 <Form.Label>Sort Order</Form.Label>
                 <Form.Control
                   type="number"
                   min="0"
                   value={takenFromForm.sort_order}
                   onChange={(e) => handleTakenFromInputChange('sort_order', parseInt(e.target.value) || 0)}
                   placeholder="0"
                 />
               </Form.Group>

               <Form.Group className="mb-3">
                 <Form.Check
                   type="checkbox"
                   label="Active"
                   checked={takenFromForm.active}
                   onChange={(e) => handleTakenFromInputChange('active', e.target.checked)}
                 />
               </Form.Group>
            </Form>
          </Modal.Body>
          
          <Modal.Footer>
            <Button variant="secondary" onClick={handleCloseTakenFromModal}>
              Cancel
            </Button>
            <Button 
              variant="primary" 
              onClick={handleSaveTakenFrom}
              disabled={savingTakenFrom}
            >
              {savingTakenFrom ? 'Saving...' : 'Save'}
            </Button>
          </Modal.Footer>
        </Modal>
      </div>
    </Container>
  );
}

export default SetupWorkOrdersScreen;
